import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import UndoRedo from './UndoRedo';
import SearchBar from './SearchBar';
import LockControl from '../LockControl';
import {
  MagnifyingGlassIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';

/**
 * Main Toolbar Component
 * 
 * Top toolbar with all editing controls
 */
const Toolbar = ({
  // Undo/Redo
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  
  // Search
  onSearch,
  onReplace,
  onNextSearch,
  onPreviousSearch,
  searchResults,
  currentSearchIndex,
  
  // Lock
  lockStatus,
  hasLock,
  onAcquireLock,
  onReleaseLock,
  onForceOverride,
  lockLoading,
  
  // Navigation
  sessionTitle,
  backUrl,
  
  className = '',
}) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  /**
   * Handle search open
   */
  const handleSearchOpen = () => {
    setIsSearchOpen(true);
  };

  /**
   * Handle search close
   */
  const handleSearchClose = () => {
    setIsSearchOpen(false);
  };

  return (
    <div className={`sticky top-0 z-50 flex-shrink-0 bg-white border-b border-gray-200 shadow-sm ${className}`}>
      <div className="flex items-center justify-between px-4 py-3">
        {/* Left: Back Button & Session Title */}
        <div className="flex items-center gap-4">
          {backUrl && (
            <Link
              to={backUrl}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Back to Session Details
            </Link>
          )}
          {sessionTitle && (
            <h1 className="text-lg font-semibold text-gray-900">{sessionTitle}</h1>
          )}
        </div>

        {/* Center: Lock Control */}
        <div className="flex-1 flex justify-center px-4">
          <LockControl
            lockStatus={lockStatus}
            hasLock={hasLock}
            onAcquireLock={onAcquireLock}
            onReleaseLock={onReleaseLock}
            onForceOverride={onForceOverride}
            isLoading={lockLoading}
          />
        </div>

        {/* Right: Undo/Redo, Search */}
        <div className="flex items-center gap-4">
          {/* Undo/Redo */}
          <UndoRedo
            onUndo={onUndo}
            onRedo={onRedo}
            canUndo={canUndo}
            canRedo={canRedo}
          />

          {/* Search */}
          <button
            onClick={handleSearchOpen}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="Search (Cmd+F)"
          >
            <MagnifyingGlassIcon className="h-5 w-5" />
            Search
          </button>
        </div>
      </div>

      {/* Search Bar (conditional) */}
      <SearchBar
        isOpen={isSearchOpen}
        onClose={handleSearchClose}
        onSearch={onSearch}
        onReplace={onReplace}
        onNext={onNextSearch}
        onPrevious={onPreviousSearch}
        results={searchResults}
        currentIndex={currentSearchIndex}
      />
    </div>
  );
};

export default Toolbar;


